import { keepPreviousData } from "@tanstack/react-query";
import compact from "lodash/compact";
import every from "lodash/every";
import { stringify } from "PFCore/helpers/use_deterministic_stringify";
import { useOptions } from "PFCore/hooks/queries/options";
import { areOptionsGrouped as areOptionsGroupCheck } from "PFCore/hooks/queries/options/use_options.utils";
import { GroupOption, Option, OptionOriginal, SelectOptions } from "PFTypes/options";
import { useCallback, useMemo } from "react";

import { SelectProps } from "../select.types";
import { getIds } from "../select.utils";
import { useSelectedOptions } from "../use_options";
import { getSelectAllOption } from "./get_select_all_option";
import { ResultOption } from "./types";

type UseDropdownOptions<T> = {
  searchTerm: string;
  isDropdownOpen: boolean;
  dropdownId: string;
  isSearchable: boolean;
} & Pick<
  SelectProps<T>,
  | "searchId"
  | "onChange"
  | "value"
  | "multiple"
  | "options"
  | "fetchOptions"
  | "cacheTime"
  | "parseOptions"
  | "limit"
>;

export type UseDropdownOptionsReturn<T> = {
  resultOptions: SelectOptions<T>;
  selectedOptions: Option<T>[];
  isFetchingAvailableOptions: boolean;
  isFetchingSelectedOptions: boolean;
  isFetchingOptionsEnabled: boolean;
  isMoreOptions: boolean;
  areOptionsGrouped: boolean;
  selectedOptionsLimit?: number;
  isSelectedLimitReached: boolean;
};

export const useDropdownOptions = <T extends OptionOriginal = OptionOriginal>({
  value,
  options,
  fetchOptions,
  parseOptions,
  searchTerm,
  isDropdownOpen,
  dropdownId,
  cacheTime,
  multiple,
  onChange,
  isSearchable,
  limit,
  searchId
}: UseDropdownOptions<T>): UseDropdownOptionsReturn<T> => {
  const stringifiedValue = stringify(value);
  const selectedOptionIds = useMemo(() => getIds(value), [stringifiedValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    data: selectedOptions,
    isFetching: isFetchingSelectedOptions,
    limit: selectedOptionsLimit,
    isLimitReached: isSelectedLimitReached
  } = useSelectedOptions({
    dropdownId,
    selectedOptionIds,
    options,
    limit,
    fetchOptions,
    queryOptions: {
      placeholderData: keepPreviousData,
      gcTime: cacheTime
    }
  });

  const maybeDisableUnselectedOptions = useCallback(
    (options: Option<T>[] | GroupOption<T>[]): Option<T>[] | GroupOption<T>[] =>
      options.map((entry) => ({
        ...entry,
        disabled: entry.disabled || (!selectedOptionIds.includes(entry.id) && isSelectedLimitReached)
      })),
    [selectedOptionIds, isSelectedLimitReached]
  );

  const {
    data: availableOptions,
    isFetching: isFetchingAvailableOptions,
    total: totalAvailableOptions
  } = useOptions({
    dropdownId,
    searchId,
    searchTerm,
    options,
    fetchOptions,
    parseOptions: (options) => {
      const locallyParsedOptions = maybeDisableUnselectedOptions(options);
      return parseOptions ? parseOptions(locallyParsedOptions) : locallyParsedOptions;
    },
    queryOptions: {
      enabled: isDropdownOpen,
      gcTime: cacheTime
    }
  });

  const isFetchingOptionsEnabled = !!fetchOptions;

  const isMoreOptions = availableOptions.length < totalAvailableOptions;

  const areOptionsGrouped = useMemo(
    () => !fetchOptions && areOptionsGroupCheck(options),
    [options, fetchOptions]
  );

  const isEverySelectedOptionsIncludedInAvailableOptions = useMemo(() => {
    const availableOptionsIds = availableOptions.map(({ id }) => id);
    return every(selectedOptionIds, (id) => availableOptionsIds.includes(id));
  }, [availableOptions, selectedOptionIds]);

  const resultOptions = useMemo(() => {
    if (areOptionsGrouped) {
      return availableOptions;
    }
    if (
      !isFetchingAvailableOptions &&
      !isEverySelectedOptionsIncludedInAvailableOptions &&
      !searchTerm.trim() &&
      isFetchingOptionsEnabled &&
      multiple
    ) {
      return selectedOptions;
    }
    if (isFetchingAvailableOptions) {
      return [];
    }
    const selectAllOption = getSelectAllOption<T>({
      availableOptions,
      multiple,
      selectedOptions,
      selectedOptionIds,
      onChange,
      isSearchable,
      selectedOptionsLimit
    });
    return compact([selectAllOption, ...availableOptions]) as ResultOption<T>[];
  }, [
    areOptionsGrouped,
    availableOptions,
    isEverySelectedOptionsIncludedInAvailableOptions,
    searchTerm,
    selectedOptions,
    selectedOptionsLimit,
    selectedOptionIds,
    isFetchingAvailableOptions,
    isFetchingOptionsEnabled,
    isSearchable,
    multiple,
    onChange
  ]);

  return {
    resultOptions,
    selectedOptions,
    selectedOptionsLimit,
    isFetchingAvailableOptions,
    isFetchingSelectedOptions,
    isFetchingOptionsEnabled,
    isMoreOptions,
    isSelectedLimitReached,
    areOptionsGrouped
  };
};
